<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  
  let subscriptions = [];
  let isLoading = true;
  let error = null;

  onMount(async () => {
    try {
      // The backend endpoint sorts by the latest record by default
      const response = await authFetch(getApiUrl('/subscriptions/all'));
      if (!response.ok) {
        throw new Error('Failed to fetch subscription data.');
      }
      subscriptions = await response.json();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  });

  // Helper to format dates for display
  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // Helper to format currency
  function formatCurrency(amount, currency) {
    if (amount == null || !currency) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  }
</script>

<div class="admin-container">
  <h2>All User Subscriptions</h2>
  <p>This page lists all membership subscriptions purchased by users, sorted by the most recent.</p>

  {#if isLoading}
    <p>Loading subscriptions...</p>
  {:else if error}
    <p class="error">Error: {error}</p>
  {:else if subscriptions.length === 0}
    <p>No user subscriptions have been recorded yet.</p>
  {:else}
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>User Display Name</th>
            <th>Membership Plan</th>
            <th>Amount Paid</th>
            <th>Start Date</th>
            <th>Expiry Date</th>
            <th>Stripe Payment</th>
          </tr>
        </thead>
        <tbody>
          {#each subscriptions as sub}
            <tr>
              <td>{sub.displayName || 'N/A'}</td>
              <td>{sub.membershipName || 'Unknown Plan'}</td>
              <td>{formatCurrency(sub.paidAmount, sub.paidCurrency)}</td>
              <td>{formatDate(sub.startDate)}</td>
              <td>{formatDate(sub.expiryDate)}</td>
              <td>
                {#if sub.paymentIntentId}
                  <a href={`https://dashboard.stripe.com/payments/${sub.paymentIntentId}`} target="_blank" rel="noopener noreferrer">
                    View on Stripe
                  </a>
                {:else}
                  N/A
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<style>
  .admin-container {
    width: 100%;
    max-width: 960px;
    margin: 2rem auto;
    text-align: left;
  }

  .table-wrapper {
    overflow-x: auto;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
    border: 1px solid #dee2e6;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
  }

  th, td {
    padding: 12px 15px;
    text-align: left;
  }

  thead {
    background-color: #f8f9fa;
  }

  th {
    font-weight: 600;
    color: #495057;
  }

  tbody tr {
    border-bottom: 1px solid #dee2e6;
  }

  tbody tr:last-child {
    border-bottom: none;
  }
  
  .error {
    color: red;
    font-weight: bold;
  }
</style>